import { mineAPI } from './api/MineAPI.js';
import { filterAPI } from './api/FilterAPI.js';
import { statisticsAPI } from './api/StatisticsAPI.js';
import { countryAPI } from './api/CountryAPI.js';
import { eventsAPI } from './api/EventsAPI.js';

/**
 * APIClient - Coordinating facade for all API modules
 *
 * Provides a unified interface that delegates to focused API modules.
 * Maintains backward compatibility while using the new modular structure.
 */
export class APIClient {
  constructor() {
    this.mineAPI = mineAPI;
    this.filterAPI = filterAPI;
    this.statisticsAPI = statisticsAPI;
    this.countryAPI = countryAPI;
    this.eventsAPI = eventsAPI;
  }

  // Mine-related endpoints (delegate to MineAPI)
  async fetchMinesInViewport(params) {
    return this.mineAPI.fetchMinesInViewport(params);
  }

  async searchMines(query) {
    return this.mineAPI.searchMines(query);
  }

  async getMineDetails(mineId) {
    return this.mineAPI.getMineDetails(mineId);
  }

  async searchSuggestions(query, options = {}) {
    return this.mineAPI.searchSuggestions(query, options);
  }

  // Filter-related endpoints (delegate to FilterAPI)
  async fetchFilterOptions() {
    return this.filterAPI.fetchFilterOptions();
  }

  async fetchCountries() {
    return this.filterAPI.fetchCountries();
  }

  async fetchCommodities() {
    return this.filterAPI.fetchCommodities();
  }

  // Unified filter options method
  async getFilterOptions(filterType) {
    return this.filterAPI.getFilterOptions(filterType);
  }

  // Legacy methods for backward compatibility - use unified method internally
  async getCountryFilterOptions() {
    return this.filterAPI.getCountryFilterOptions();
  }

  async getCommodityFilterOptions() {
    return this.filterAPI.getCommodityFilterOptions();
  }

  async getMineTypeFilterOptions() {
    return this.filterAPI.getMineTypeFilterOptions();
  }

  async getMineStatusFilterOptions() {
    return this.filterAPI.getMineStatusFilterOptions();
  }

  // Statistics endpoints (delegate to StatisticsAPI)
  async getStatistics() {
    return this.statisticsAPI.getStatistics();
  }

  async getFilteredStatistics(filters) {
    return this.statisticsAPI.getFilteredStatistics(filters);
  }

  // Country-related endpoints (delegate to CountryAPI)
  async searchCountries(query, options = {}) {
    return this.countryAPI.searchCountries(query, options);
  }

  async getCountryBounds(identifier) {
    return this.countryAPI.getCountryBounds(identifier);
  }

  async getCountryDetails(identifier) {
    return this.countryAPI.getCountryDetails(identifier);
  }

  async getCountries(options = {}) {
    return this.countryAPI.getCountries(options);
  }

  // Events-related endpoints (delegate to EventsAPI)
  async getNewsForMine(mineId, options = {}) {
    return this.eventsAPI.getNewsForMine(mineId, options);
  }

  async getEarthquakesForMine(mineId, options = {}) {
    return this.eventsAPI.getEarthquakesForMine(mineId, options);
  }

  async getMineEvents(mineId, options = {}) {
    return this.eventsAPI.getMineEvents(mineId, options);
  }
}

// Export singleton instance for backward compatibility
export const apiClientInstance = new APIClient();
export const apiClient = apiClientInstance;

// Export mock for testing (if needed)
export const __mockApiClient = apiClientInstance;